// Doc synthesis: one graph, many projections (PRD §13) — render the same
// trace at different length/detail without redoing the thinking.

import { state, type DecisionNode } from "./state";

export interface Projection { label: string; max?: number; instr?: string }

export const PROJECTIONS: Record<string, Projection> = {
  onepager: {
    label: "One-pager", max: 1100,
    instr: `Write a tight ONE-PAGER. H1 title, then:
- **Summary** — 2-3 sentences, grounded in the scalars.
- **Decisions** — one compact bullet per decision: choice — one-clause why.
- **Risks** — 2-4 bullets, including any replay findings.
No architecture walkthrough, no filler. Scannable in 30 seconds.`,
  },
  prd: {
    label: "PRD", max: 2000,
    instr: `Start with an H1 title, then these sections in order:
- **Summary** — one paragraph, grounded in the scalars.
- **Architecture** — 3-6 NUMBERED steps tracing the request/data path through the chosen components (synthesise a coherent flow; name the components). If not request-serving, give the component layout.
- **Decisions & Rationale** — each decision → choice → why, citing the scalars and naming the rejected alternative.
- **Key Tradeoffs** — the load-bearing ones only.
- **Non-goals** — 2-3 bullets bounding scope.
- **Open Questions / Risks** — plus a light validation pass on weak/under-grounded decisions.`,
  },
  spec: {
    label: "Tech spec", max: 2047,
    instr: `Write a DETAILED technical spec for the implementing engineer/agent. H1 title, then:
- **Summary**
- **Architecture** — numbered request/data path AND each key component's responsibility.
- **Data & Interfaces** — the data structures/schemas and API/queue contracts implied by the decisions.
- **Decisions & Rationale** — each decision → choice → why (cite scalars) + the rejected alternative.
- **Failure Modes & Mitigations** — what breaks at these numbers and the guard.
- **Rollout & Metrics** — how to ship safely and the SLOs to watch.
- **Open Questions / Risks** — fold in the replay findings.
Be precise and concrete; name technologies and give shapes where implied.`,
  },
  template: {
    label: "Template",   // the full Marqo template accumulated by the live doc — assembled locally, instant
  },
};

/** Depth-first assembly of every resolved decision, grouped by branch. */
export function traceText(): string {
  const lines: string[] = [];
  const root = state.rootId ? state.nodes.get(state.rootId) : null;
  if (!root) return "";
  const walk = (n: DecisionNode, prefix: string) => {
    if (n.chosenIndex != null) {
      const opt = n.options[n.chosenIndex]!;
      lines.push(`${prefix}[${n.branch || "root"}] ${n.question}\n${prefix}  → CHOSE: ${opt.label} — ${opt.tradeoff}`);
    }
    Object.values(n.childId).forEach((cid) => {
      const child = state.nodes.get(cid);
      if (child) walk(child, prefix);
    });
  };
  walk(root, "");
  return lines.join("\n");
}
